import RangeSteps from '@scripts/components/RangeSteps';
import FormPartial from '@scripts/forms/FormPartial';
import CvRiskHelpers from '@scripts/helpers';

/**
 * "Personal history" form partial
 */
export default class extends FormPartial {
  /**
   * Atherosclerotic Cardiovascular Disease
   *
   * @private
   *
   * @type {HTMLLabelElement}
   */
  #ascvd;

  /**
   * Atherosclerotic Cardiovascular Disease
   *
   * @private
   *
   * @type {HTMLInputElement}
   */
  #ascvdRiskFactors;

  /**
   * Diabetes mellitus
   *
   * @private
   *
   * @type {HTMLLabelElement}
   */
  #dm;

  /**
   * Duration of diabetes
   *
   * @private
   *
   * @type {HTMLSelectElement}
   */
  #dmDuration;

  /**
   * Organ damage
   *
   * @private
   *
   * @type {RangeSteps}
   */
  #organDamage;

  /**
   * Familial hypercholesterolaemia
   *
   * @private
   *
   * @type {HTMLLabelElement}
   */
  #fh;

  /**
   * Smoker
   *
   * @private
   *
   * @type {HTMLLabelElement}
   */
  #smoker;

  /**
   * Chronic kidney disease
   *
   * @private
   *
   * @type {RangeSteps}
   */
  #ckd;

  constructor(form) {
    super(form);

    this.#ascvd = this._form.element.querySelector('#cvriskHistoryAscvd');
    this.#ascvdRiskFactors = this._form.element.querySelector(
      '#cvriskAscvdRiskFactors'
    );
    this.#dm = this._form.element.querySelector('#cvriskHistoryDm');
    this.#dmDuration = this._form.element.querySelector(
      '#cvriskHistoryDmDuration'
    );
    this.#organDamage = new RangeSteps(
      this._form.element.querySelector('#cvriskHistoryOrgDam')
    );
    this.#fh = this._form.element.querySelector('#cvriskHistoryFh');
    this.#smoker = this._form.element.querySelector('#cvriskHistorySmoker');
    this.#ckd = new RangeSteps(
      this._form.element.querySelector('#cvriskHistoryCkd')
    );
  }

  /**
   * Handler for changes of the ASCVD Risk Factors
   * - check the box if it's displayed and the patient has risk factors
   *
   * @private
   *
   * @return {void}
   */
  #handlerAscvdRiskFactorsUpdated() {
    if (
      // parent is visible
      this.#ascvdRiskFactors.closest('.cvrisk-toggle-target.cvrisk-active') &&
      // has risk factors
      this._form.hasAscvdRiskFactors()
    ) {
      this.#ascvdRiskFactors.checked = true;
    }
  }

  initialize() {
    // on diabetes or duration change
    this.#dm.addEventListener('change', () => this.toggleAscvdRiskFactors());
    this.#dmDuration.addEventListener('change', () =>
      this.toggleAscvdRiskFactors()
    );
    // ASCVD risk factors
    this._form.on('update-ascvd-risk-factors', () =>
      this.#handlerAscvdRiskFactorsUpdated()
    );
    this.#smoker.addEventListener('change', () =>
      this.#handlerAscvdRiskFactorsUpdated()
    );
    this.toggleAscvdRiskFactors(false);
  }

  /**
   * Show/Hide ASCVD risk factors checkbox
   *
   * @param {bool} check Check the box if is shown
   *
   * @return {void}
   */
  toggleAscvdRiskFactors(check = true) {
    const parent = this.#ascvdRiskFactors.closest('.cvrisk-toggle-target');
    if (!parent) {
      return;
    }

    const factors = window?.CvRisk?.ascvd?.riskFactors;
    const hide =
      // no factors
      !factors ||
      factors.length <= 0 ||
      // no diabetes
      !CvRiskHelpers.isToggleChecked(this.#dm) ||
      !this.#dmDuration.value ||
      // duration not in list
      factors.indexOf(this.#dmDuration.value) === -1;

    if (!hide) {
      parent.classList.add('cvrisk-active');

      if (check && this._form.hasAscvdRiskFactors()) {
        this.#ascvdRiskFactors.checked = true;
      }
    } else {
      parent.classList.remove('cvrisk-active');
    }
  }

  validate() {
    this._resetMessages();

    let errors = [];

    // if has diabetes
    if (CvRiskHelpers.isToggleChecked(this.#dm)) {
      // Duration of diabetes
      if (!this.#dmDuration.value) {
        errors.push(CvRiskHelpers.error('dm_duration_required'));
      }
    }

    this.addErrors(errors);

    return super.validate();
  }

  reset() {
    super.reset();

    /**
     * Reset Toggle components
     *
     * ASCVD
     * Diabetes mellitus
     * Familial hypercholesterolaemia
     * Smoker
     */
    [this.#ascvd, this.#dm, this.#fh, this.#smoker].forEach((label) => {
      label
        .querySelectorAll('input[type="checkbox"]:checked')
        .forEach((input) => {
          input.checked = false;
          // for toggle
          input.dispatchEvent(new Event('change'));
        });
    });

    /**
     * Reset Select elements
     *
     * Duration of diabetes
     */
    [this.#dmDuration].forEach(
      (select) => (select.value = select.querySelector('option').value)
    );

    /**
     * Reset RangeSteps components
     *
     * CKD
     * Organ damage
     */
    [this.#ckd, this.#organDamage].forEach((range) => range.reset());
  }

  hasAscvdRiskFactors() {
    return CvRiskHelpers.getToggleValue(this.#smoker) === '1';
  }
}
